import {
  type TrustCheckViewModel,
  useTrustChecksViewModel,
} from "@/hooks/matter-view-models/use-trust-checks-view-model";
import { retrieveMatterQueryOptions } from "@/lib/data/react-query/matters/matter";
import type { Connection, MedicalTreatmentBill } from "@/lib/types";
import { getMedicalTreatmentContactConnection } from "@/lib/utils/connections/medical-treatment-contact-connections";
import { useSuspenseQuery } from "@tanstack/react-query";

export type MedicalTreatmentBillViewModel = MedicalTreatmentBill & {
  trustChecks: TrustCheckViewModel[];
  provider: Connection | undefined;
  hasReference: boolean;
};

export const useMedicalTreatmentBillViewModel = (
  matterId: string,
  treatmentId?: string,
) => {
  const { data: matterData } = useSuspenseQuery(
    retrieveMatterQueryOptions(matterId),
  );
  const matter = matterData.data;
  const trustChecksViewModel = useTrustChecksViewModel(matterId);

  return matter.medicalTreatmentBills
    .filter((bill) => {
      if (treatmentId) {
        return bill.medicalTreatmentId === treatmentId;
      }
      return true;
    })
    .map((bill) => {
      const trustChecks = trustChecksViewModel.filter((trustCheck) =>
        trustCheck.connections.some(
          (connection) => connection.medicalTreatmentBillId === bill.id,
        ),
      );

      const treatment = matter.medicalTreatments.find(
        (item) => item.id === bill.medicalTreatmentId,
      );
      const provider = getMedicalTreatmentContactConnection(
        treatment,
        "Provider",
      );
      const hasReference = matter.fileReferences.some(
        (reference) => reference.medicalTreatmentBillId === bill.id,
      );
      return {
        ...bill,
        trustChecks,
        provider,
        hasReference,
      } as MedicalTreatmentBillViewModel;
    });
};
